import React, { useState, useEffect, useRef } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Support = () => {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showChatModal, setShowChatModal] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterState, setFilterState] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [chatsPerPage] = useState(10);

  const latestChatsRef = useRef([]);

  // Poll for new messages every 10 seconds
  useEffect(() => {
    fetchChats(); // Initial fetch
    
    const pollInterval = setInterval(() => {
      fetchChats();
    }, 10000); // 10 seconds

    // Cleanup interval on component unmount
    return () => clearInterval(pollInterval);
  }, []);

  // Add notification sound for new messages
  useEffect(() => {
    const audio = new Audio('/notification.mp3');
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // When tab is not visible, check more frequently
        clearInterval(window.pollInterval);
        window.pollInterval = setInterval(fetchChats, 5000); // 5 seconds
      } else {
        // When tab is visible, check less frequently
        clearInterval(window.pollInterval);
        window.pollInterval = setInterval(fetchChats, 10000); // 10 seconds
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Play sound when new messages arrive
    const handleNewMessage = () => {
      if (document.hidden) {
        audio.play().catch(err => console.log('Error playing notification:', err));
      }
    };

    window.addEventListener('newMessage', handleNewMessage);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('newMessage', handleNewMessage);
      clearInterval(window.pollInterval);
    };
  }, []);

  const fetchChats = async () => {
    try {
      const response = await fetch('/api/chats');
      if (!response.ok) {
        throw new Error('Failed to fetch chats');
      }
      const data = await response.json();
      
      // Compare with previous chats to check for new messages
      const hasNewMessages = data.some(newChat => {
        const existingChat = latestChatsRef.current.find(chat => chat.id === newChat.id);
        return !existingChat || new Date(newChat.date) > new Date(existingChat.date);
      });

      if (hasNewMessages) {
        // Dispatch event for new message notification
        window.dispatchEvent(new Event('newMessage'));
        
        // Show toast notification
        toast.info('New messages received!', {
          position: "top-right",
          autoClose: 3000,
        });
      }

      setChats(data);
      latestChatsRef.current = data;
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const groupChatsByConversation = (chats) => {
    const grouped = chats.reduce((acc, chat) => {
      // Group by ticketnumber, but fallback to user-based grouping if no ticket number
      let conversationKey, displayName;
      
      if (chat.ticketnumber !== null && chat.ticketnumber !== undefined && chat.ticketnumber !== '') {
        conversationKey = chat.ticketnumber.toString();
        displayName = chat.from_name || 'Unknown User';
      } else {
        // Fallback to grouping by user if no ticket number
        conversationKey = `user_${chat.from_id || chat.from_name}`;
        displayName = chat.from_name || 'Unknown User';
      }

      if (!acc[conversationKey]) {
        acc[conversationKey] = {
          conversationKey,
          username: displayName,
          ticketnumber: (chat.ticketnumber !== null && chat.ticketnumber !== undefined && chat.ticketnumber !== '') ? chat.ticketnumber : null,
          messages: [],
          lastMessage: null,
          unreadCount: 0,
          latestDate: null,
          hasUnreplied: false
        };
      }

      acc[conversationKey].messages.push(chat);

      // Update latest message info
      if (!acc[conversationKey].latestDate || new Date(chat.date) > new Date(acc[conversationKey].latestDate)) {
        acc[conversationKey].lastMessage = chat;
        acc[conversationKey].latestDate = chat.date;
        // Set nonRepliedMessage flag if last message is not from Support Team
        acc[conversationKey].nonRepliedMessage = chat.from_name !== 'Support Team';
      }

      // Count unread messages
      if (chat.state === 'unread') {
        acc[conversationKey].unreadCount++;
      }

      // Check if message is unreplied (state is 'unread' and from user, not support)
      if (chat.state === 'unread' && chat.from_name !== 'Support Team') {
        acc[conversationKey].hasUnreplied = true;
      }

      return acc;
    }, {});

    // Convert to array and sort by latest message date
    return Object.values(grouped).sort((a, b) => 
      new Date(b.latestDate) - new Date(a.latestDate)
    );
  };

  const handleChatStateChange = async (chatId, newState) => {
    try {
      const response = await fetch(`/api/chats/${chatId}/state`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ state: newState }),
      });

      if (!response.ok) {
        throw new Error('Failed to update chat state');
      }

      // Update local state
      setChats(prevChats => 
        prevChats.map(chat => 
          chat.id === chatId 
            ? { ...chat, state: newState }
            : chat
        )
      );

      toast.success('Chat state updated successfully', {
        position: "top-right",
        autoClose: 3000,
      });

    } catch (error) {
      console.error('Error updating chat state:', error);
      toast.error('Failed to update chat state', {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleFileUpload = async (files) => {
    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append('attachments', file);
    });

    try {
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload files');
      }

      const data = await response.json();
      return data.filePaths; // Array of uploaded file paths
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files', {
        position: "top-right",
        autoClose: 3000,
      });
      return [];
    }
  };

  const handleReply = async () => {
    if (!replyMessage.trim() || !selectedMessage) return;

    try {
      let attachmentPaths = [];
      
      // Upload attachments if any
      if (replyAttachments.length > 0) {
        attachmentPaths = await handleFileUpload(replyAttachments);
      }

      const response = await fetch('/api/chats', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          from_id: 'admin',
          from_name: 'Support Team',
          toid: selectedMessage.from_id,
          to_name: selectedMessage.from_name,
          message_title: `Re: ${selectedMessage.message_title}`,
          message_body: replyMessage,
      //    ticketnumber: selectedMessage.ticketnumber || selectedMessage.lastMessage?.ticketnumber || selectedMessage.conversationKey,  // Use actual ticketnumber
                ticketnumber: selectedMessage.ticketnumber ,  // Use actual ticketnumber

          category: 'support',
          state: 'sent',
          attachments: attachmentPaths
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send reply');
      }

      // Get the new message data from response
      const newMessage = await response.json();

      toast.success('Reply sent successfully!', {
        position: "top-right",
        autoClose: 3000,
      });

      // Update local state to show the new reply immediately
      setChats(prevChats => {
        // Update the chat group in chats array
        const updatedChats = prevChats.map(chat => {
          if (chat.conversationKey === selectedChat.conversationKey || chat.ticketnumber === selectedChat.ticketnumber) {
            return {
              ...chat,
              messages: [...(chat.messages || []), newMessage],
              lastMessage: newMessage,
              latestDate: newMessage.date,
              unreadCount: chat.unreadCount, // reply is from support, so unread count unchanged
              nonRepliedMessage: false,
              hasUnreplied: false,
            };
          }
          return chat;
        });
        return updatedChats;
      });

      // Also update selectedChat to include new message
      setSelectedChat(prev => ({
        ...prev,
        messages: [...prev.messages, newMessage],
        lastMessage: newMessage,
        latestDate: newMessage.date,
        nonRepliedMessage: false,
        hasUnreplied: false,
      }));

      setReplyMessage('');
      setReplyAttachments([]);
      setShowReplyModal(false);
      setShowChatModal(false);

    } catch (error) {
      if (error.response) {
        error.response.text().then(text => {
          console.error('Error sending reply response text:', text);
        });
      }
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply', {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleAttachmentChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const isValidType = file.type.startsWith('image/') || file.type === 'application/pdf';
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB limit
      
      if (!isValidType) {
        toast.error(`${file.name} is not a valid file type. Only images and PDFs are allowed.`, {
          position: "top-right",
          autoClose: 3000,
        });
        return false;
      }
      
      if (!isValidSize) {
        toast.error(`${file.name} is too large. Maximum file size is 10MB.`, {
          position: "top-right",
          autoClose: 3000,
        });
        return false;
      }
      
      return true;
    });
    
    setReplyAttachments(prev => [...prev, ...validFiles]);
  };

  const removeAttachment = (index) => {
    setReplyAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const renderAttachment = (attachment, isUrl = false) => {
    if (!attachment) return null;
    
    const fileName = isUrl ? attachment.split('/').pop() : attachment.name;
    const fileType = isUrl ? 
      (attachment.toLowerCase().includes('.pdf') ? 'application/pdf' : 'image') :
      attachment.type;
    
    if (fileType.startsWith('image/') || fileType === 'image') {
      return (
        <div style={{ marginTop: '10px' }}>
          <img 
            src={isUrl ? `${attachment}` : URL.createObjectURL(attachment)}
            alt={fileName}
            style={{
              maxWidth: '200px',
              maxHeight: '200px',
              borderRadius: '8px',
              border: '1px solid #dce4ec',
              cursor: 'pointer'
            }}
            onClick={() => {
              const url = isUrl ? `${attachment}` : URL.createObjectURL(attachment);
              window.open(url, '_blank');
            }}
          />
          <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '5px' }}>
            {fileName}
          </div>
        </div>
      );
    } else if (fileType === 'application/pdf') {
      return (
        <div style={{ 
          marginTop: '10px',
          padding: '10px',
          border: '1px solid #dce4ec',
          borderRadius: '8px',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          cursor: 'pointer'
        }}
        onClick={() => {
          const url = isUrl ? `${attachment}` : URL.createObjectURL(attachment);
          window.open(url, '_blank');
        }}>
          <span style={{ fontSize: '24px' }}>📄</span>
          <div>
            <div style={{ fontSize: '14px', fontWeight: '500' }}>{fileName}</div>
            <div style={{ fontSize: '12px', color: '#7f8c8d' }}>PDF Document</div>
          </div>
        </div>
      );
    }
    
    return null;
  };

  const handleWhatsAppSend = () => {
    if (!whatsappNumber.trim() || !whatsappMessage.trim()) {
      toast.error('Please fill in both phone number and message', {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Clean phone number (remove non-digits)
    const cleanNumber = whatsappNumber.replace(/\D/g, '');
    
    // Encode message for URL
    const encodedMessage = encodeURIComponent(whatsappMessage);
    
    // Create WhatsApp URL
    const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodedMessage}`;
    
    // Open WhatsApp Web
    window.open(whatsappUrl, '_blank');
    
    toast.success('WhatsApp Web opened successfully!', {
      position: "top-right",
      autoClose: 3000,
    });

    setWhatsappNumber('');
    setWhatsappMessage('');
    setShowWhatsAppModal(false);
  };

  const openChatModal = (chatGroup) => {

     setSelectedChat(chatGroup);
    setShowChatModal(true);
    
    // Mark all messages in this chat as read
    chatGroup.messages.forEach(message => {
      if (message.state === 'unread') {
        handleChatStateChange(message.id, 'read');
      }
    });
  };

  const openReplyModal = (message) => {
    setSelectedMessage(message);
    setShowReplyModal(true);
  };

  // Filter and search functionality
  const filteredChats = groupChatsByConversation(chats).filter(chatGroup => {
    const matchesSearch = chatGroup.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         chatGroup.lastMessage?.message_body?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterState === 'all' || 
                         (filterState === 'unread' && chatGroup.unreadCount > 0) ||
                         (filterState === 'read' && chatGroup.unreadCount === 0) ||
                         (filterState === 'nonReplied' && chatGroup.nonRepliedMessage === true);
    
    return matchesSearch && matchesFilter;
  });

  // Pagination
  const indexOfLastChat = currentPage * chatsPerPage;
  const indexOfFirstChat = indexOfLastChat - chatsPerPage;
  const currentChats = filteredChats.slice(indexOfFirstChat, indexOfLastChat);
  const totalPages = Math.ceil(filteredChats.length / chatsPerPage);

  return (
    <div style={{ 
      padding: '20px', 
      maxWidth: '1200px', 
      margin: '0 auto',
      width: '100%',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        flexWrap: 'wrap',
        gap: '20px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          flexWrap: 'wrap'
        }}>
          <a href="/Home"
            style={{
              padding: '10px 20px',
              backgroundColor: '#3498db',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#2980b9';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#3498db';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            <span style={{ fontSize: '18px' }}>←</span>
            Home
          </a>
          
          {/* Search Input */}
          <input
            type="text"
            placeholder="Search chats..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #dce4ec',
              borderRadius: '8px',
              fontSize: '14px',
              width: '200px'
            }}
          />
          
          {/* Filter Dropdown */}
          <select
            value={filterState}
            onChange={(e) => setFilterState(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #dce4ec',
              borderRadius: '8px',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Chats</option>
         
            <option value="nonReplied">Non-Replied Only</option>
          </select>
          
          {/* WhatsApp Button */}
          <button
            onClick={() => setShowWhatsAppModal(true)}
            style={{
              padding: '10px 20px',
              backgroundColor: '#25D366',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#1ebe57';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#25D366';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            <span style={{ fontSize: '18px' }}>📱</span>
            WhatsApp
          </button>
        </div>
        
        <div style={{
          backgroundColor: '#27ae60',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          Total Chats: {filteredChats.length}
        </div>
      </div>

      <h1 style={{
        color: '#2c3e50',
        textAlign: 'center',
        marginBottom: '30px',
        fontSize: '2.5rem',
        fontWeight: '300',
        textShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>Support Chat System</h1>

      {loading ? (
        <div style={{ padding: '20px', textAlign: 'center', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          Loading chats...
        </div>
      ) : error ? (
        <div style={{ padding: '20px', textAlign: 'center', color: '#e74c3c', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          {error}
        </div>
      ) : filteredChats.length === 0 ? (
        <div style={{ padding: '20px', textAlign: 'center', color: '#7f8c8d', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          No chats found
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {currentChats.map((chatGroup, index) => (
            <div key={`${chatGroup.conversationKey}-${index}`} style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              overflow: 'hidden',
              transition: 'transform 0.2s ease',
              cursor: 'pointer',
            }}
            onClick={() => openChatModal(chatGroup)}
            onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)' }}
            onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)' }}>
              
              <div style={{
                background: chatGroup.unreadCount > 0 
                  ? 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)'
                  : 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
                padding: '15px 20px',
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <div style={{ fontSize: '18px', fontWeight: '600' }}>
                  {chatGroup.username}
                  {chatGroup.ticketnumber && (
                    <span style={{ marginLeft: '8px', fontSize: '14px', fontWeight: '400', color: '#ecf0f1' }}>
                      (#{chatGroup.ticketnumber})
                    </span>
                  )}
                  {chatGroup.unreadCount > 0 && (
          <span style={{
            marginLeft: '10px',
            backgroundColor: 'rgba(255,255,255,0.3)',
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '500',
            color: chatGroup.hasUnreplied ? '#ffcc00' : 'inherit',
            boxShadow: chatGroup.hasUnreplied ? '0 0 8px 2px #ffcc00' : 'none'
          }}>
            {chatGroup.unreadCount} new
          </span>
                  )}
                  {chatGroup.nonRepliedMessage && (
                    <span style={{
                      marginLeft: '10px',
                      backgroundColor: '#ff6347',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: 'white',
                      boxShadow: '0 0 8px 2px #ff6347',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      animation: 'pulse 1.5s infinite'
                    }}>
                      <i className="fa fa-envelope" style={{ fontSize: '16px' }}></i>
                      Non-Replied Message
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '14px' }}>
                  {formatDate(chatGroup.latestDate)}
                </div>
              </div>
              
              <div style={{ padding: '20px' }}>
                <div style={{ fontSize: '16px', color: '#34495e', marginBottom: '10px' }}>
                  <strong>Latest Message:</strong> {chatGroup.lastMessage?.message_title}
                </div>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#7f8c8d',
                  lineHeight: '1.5',
                  maxHeight: '60px',
                  overflow: 'hidden'
                }}>
                  {chatGroup.lastMessage?.message_body}
                </div>
                <div style={{ 
                  marginTop: '15px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600',
                    backgroundColor: chatGroup.hasUnreplied ? '#ffcc00' : (chatGroup.unreadCount > 0 ? '#e74c3c' : '#27ae60'),
                    color: chatGroup.hasUnreplied ? '#333' : 'white',
                    boxShadow: chatGroup.hasUnreplied ? '0 0 8px 2px #ffcc00' : 'none',
                  }}>
                    {chatGroup.hasUnreplied ? 'Unreplied' : (chatGroup.unreadCount > 0 ? 'Needs Reply' : '')}
                  </span>
                  <div style={{ fontSize: '14px', color: '#95a5a6' }}>
                    {chatGroup.messages.length} message{chatGroup.messages.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && !error && filteredChats.length > 0 && totalPages > 1 && (
        <div style={{ 
          padding: '20px',
          display: 'flex',
          justifyContent: 'center',
          gap: '10px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          marginTop: '20px'
        }}>
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            style={{
              padding: '8px 16px',
              backgroundColor: currentPage === 1 ? '#e9ecef' : '#3498db',
              color: currentPage === 1 ? '#6c757d' : 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            Previous
          </button>
          <span style={{ 
            display: 'flex', 
            alignItems: 'center',
            color: '#2c3e50',
            fontSize: '14px'
          }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage >= totalPages}
            style={{
              padding: '8px 16px',
              backgroundColor: currentPage >= totalPages ? '#e9ecef' : '#3498db',
              color: currentPage >= totalPages ? '#6c757d' : 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            Next
          </button>
        </div>
      )}

      {/* Chat Details Modal with Reply Modal nested */}
      {showChatModal && selectedChat && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            position: 'relative'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: '#2c3e50' }}>
                Chat with {selectedChat.username}
                {selectedChat.ticketnumber && selectedChat.ticketnumber !== selectedChat.username && (
                  <span style={{ marginLeft: '8px', fontSize: '16px', fontWeight: '400', color: '#7f8c8d' }}>
                    (Ticket #{selectedChat.ticketnumber})
                  </span>
                )}
              </h2>
              <button
                onClick={() => setShowChatModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '5px',
                  color: '#95a5a6',
                }}
              >
                ×
              </button>
            </div>
            
            {/* Reply form integrated inside chat modal */}
            {showReplyModal && (
              <div style={{
                borderBottom: '1px solid #dce4ec',
                paddingBottom: '20px',
                marginBottom: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '15px'
              }}>
                
                <textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Type your reply here..."
                  style={{
                    width: '100%',
                    height: '150px',
                    padding: '12px',
                    border: '1px solid #dce4ec',
                    borderRadius: '8px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
                
                {/* File Upload Section */}
                <div>
                  <label style={{
                    display: 'inline-block',
                    padding: '8px 16px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dce4ec',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#495057'
                  }}>
                    <span style={{ marginRight: '8px' }}>📎</span>
                    Attach Files (Images/PDFs)
                    <input
                      type="file"
                      multiple
                      accept="image/*,.pdf"
                      onChange={handleAttachmentChange}
                      style={{ display: 'none' }}
                    />
                  </label>
                  <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '5px' }}>
                    Maximum file size: 10MB per file
                  </div>
                  
                  {/* Display selected attachments */}
                  {replyAttachments.length > 0 && (
                    <div style={{ marginTop: '15px' }}>
                      <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '10px' }}>
                        Selected Files:
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                        {replyAttachments.map((file, index) => (
                          <div key={index} style={{ position: 'relative' }}>
                            {renderAttachment(file)}
                            <button
                              onClick={() => removeAttachment(index)}
                              style={{
                                position: 'absolute',
                                top: '-5px',
                                right: '-5px',
                                backgroundColor: '#e74c3c',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                fontSize: '12px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setShowReplyModal(false)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#95a5a6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReply}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#27ae60',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Send Reply
                  </button>
                </div>
              </div>
            )}

            {!showReplyModal && (
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginBottom: '20px' }}>
                <button
                  onClick={() => {
                    openReplyModal(selectedChat.lastMessage);
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#27ae60',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Reply
                </button>
              </div>
            )}
            <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '20px' }}>
              {selectedChat.messages.map((message, index) => (
                <div key={message.id} style={{
                  marginBottom: '15px',
                  padding: '15px',
                  backgroundColor: message.from_name === 'Support Team' ? '#e3f2fd' : '#f8f9fa',
                  borderRadius: '8px',
                  borderLeft: `4px solid ${message.from_name === 'Support Team' ? '#2196f3' : '#27ae60'}`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <strong style={{ color: '#2c3e50' }}>{message.message_title}</strong>
                    <span style={{ fontSize: '12px', color: '#7f8c8d' }}>
                      {formatDate(message.date)}
                    </span>
                  </div>
                  <div style={{ color: '#34495e', lineHeight: '1.5' }}>
                    {message.message_body}
                  </div>
                  
                  {/* Display attachments */}
                  {message.attachments && message.attachments.length > 0 && (
                    <div style={{ marginTop: '10px' }}>
                      <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '5px' }}>
                        Attachments:
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                        {message.attachments.map((attachment, attachIndex) => (
                          <div key={attachIndex}>
                            {renderAttachment(attachment, true)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#95a5a6' }}>
                    From: {message.from_name} | Status: {message.state}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Modal */}
      {showWhatsAppModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#2c3e50' }}>Send WhatsApp Message</h3>
              <button
                onClick={() => setShowWhatsAppModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '5px',
                  color: '#95a5a6',
                }}
              >
                ×
              </button>
            </div>
            
            <input
              type="text"
              value={whatsappNumber}
              onChange={(e) => setWhatsappNumber(e.target.value)}
              placeholder="Phone number (e.g., +1234567890)"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #dce4ec',
                borderRadius: '8px',
                fontSize: '14px',
                marginBottom: '15px'
              }}
            />
            
            <textarea
              value={whatsappMessage}
              onChange={(e) => setWhatsappMessage(e.target.value)}
              placeholder="Type your message here..."
              style={{
                width: '100%',
                height: '120px',
                padding: '12px',
                border: '1px solid #dce4ec',
                borderRadius: '8px',
                fontSize: '14px',
                resize: 'vertical',
                marginBottom: '20px'
              }}
            />
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowWhatsAppModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleWhatsAppSend}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#25D366',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Send via WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default Support;
